/**
 * CR-6724: expected-columns panel and Download Template button for one import
 * step.
 *
 * Both the badge list and the workbook come from the backend column spec — the
 * same definition the importers validate against. The list this replaced was
 * typed by hand in `DataImport.tsx` and had drifted badly: columns that no
 * importer read, and mandatory columns that were never shown at all.
 */
import { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../../components/ui/tooltip';
import { AlertCircle, Download, Loader2 } from 'lucide-react';
import { dataImportAPI, type ImportTemplateSpec } from '../../lib/api';
import { useTemplateDownload } from './useTemplateDownload';

interface ImportTemplateSectionProps {
  /** Wizard step whose columns and template to show. */
  step: number;
  /** Import type name, used in the download toast. */
  typeName: string;
}

/**
 * Expected columns plus the Download Template action for one import step.
 *
 * @param step - Wizard step number (1-13)
 * @param typeName - Display name of the import type
 */
export default function ImportTemplateSection({
  step, typeName,
}: ImportTemplateSectionProps) {
  const [spec, setSpec] = useState<ImportTemplateSpec | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const { downloadingStep, downloadTemplate } = useTemplateDownload();
  const downloading = downloadingStep === step;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadFailed(false);

    dataImportAPI.getTemplates()
      .then(res => {
        if (cancelled) return;
        const match = res.data.steps.find(s => s.step === step) ?? null;
        setSpec(match);
        setLoadFailed(!match);
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [step]);

  const required = spec?.columns.filter(c => c.required) ?? [];
  // Columns aimed at ERP extracts are documented on the workbook's Column
  // Guide sheet rather than listed here, which would bury the ones a user
  // actually fills in — Purchase Orders alone carries twenty of them.
  const optional = spec?.columns.filter(c => !c.required && !c.advanced) ?? [];
  const advancedCount = spec?.columns.filter(c => c.advanced).length ?? 0;

  return (
    <div className="p-3 bg-muted/30 rounded-lg space-y-3">
      <div className="flex items-start justify-between gap-3">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          Expected Excel Columns
        </p>
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs px-2.5 shrink-0"
          onClick={() => downloadTemplate(step, typeName)}
          disabled={downloading}
        >
          {downloading ? (
            <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
          ) : (
            <Download className="h-3 w-3 mr-1.5" />
          )}
          Download Template
        </Button>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" />
          Loading columns...
        </div>
      )}

      {!loading && loadFailed && (
        <div className="flex items-start gap-2 text-xs text-muted-foreground">
          <AlertCircle className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-px" />
          <span>
            Could not load the column list. Download the template — it contains
            the correct headers and a Column Guide.
          </span>
        </div>
      )}

      {!loading && !loadFailed && spec && (
        <TooltipProvider delayDuration={200}>
          <div className="space-y-2.5">
            <ColumnGroup
              label="Required"
              columns={required}
              badgeClass="bg-red-50 text-red-700 border-red-200 hover:bg-red-50"
            />
            {optional.length > 0 && (
              <ColumnGroup
                label="Optional"
                columns={optional}
                badgeClass="bg-background text-muted-foreground border-border hover:bg-background"
              />
            )}
            {advancedCount > 0 && (
              <p className="text-[11px] text-muted-foreground">
                {advancedCount} further ERP {advancedCount === 1 ? 'column is' : 'columns are'} accepted
                {' '}— see the Column Guide sheet in the template.
              </p>
            )}
          </div>
        </TooltipProvider>
      )}
    </div>
  );
}

interface ColumnGroupProps {
  label: string;
  columns: { name: string; help: string; accepts: string[] }[];
  badgeClass: string;
}

/** One labelled row of column badges, each explaining itself on hover. */
function ColumnGroup({ label, columns, badgeClass }: ColumnGroupProps) {
  if (columns.length === 0) return null;
  return (
    <div>
      <p className="text-[11px] font-medium text-muted-foreground mb-1.5">
        {label}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {columns.map(col => (
          <Tooltip key={col.name}>
            <TooltipTrigger asChild>
              <Badge
                variant="outline"
                className={`text-xs font-normal cursor-help ${badgeClass}`}
              >
                {col.name}
              </Badge>
            </TooltipTrigger>
            {(col.help || col.accepts.length > 0) && (
              <TooltipContent className="max-w-xs">
                <p className="text-xs">{col.help}</p>
                {col.accepts.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Also accepted as: {col.accepts.join(', ')}
                  </p>
                )}
              </TooltipContent>
            )}
          </Tooltip>
        ))}
      </div>
    </div>
  );
}
